import type { ReactNode } from 'react';
import { IconSizes } from '@newcore/icons';
import { Card } from '@newcore/ui/card';
import { Spinner } from '@newcore/ui/loading';
import { Page } from '@/components/ui/page';
import { StatusMessage } from '@/components/ui/status-message';
import { ACCESS_LEVEL_COPY } from '../constants';
import { useConsent } from '../hooks/use-consent';
import { nonMcpScopes } from '../utils';
import { ConsentAccessLevels } from './consent-access-levels';
import { ConsentActions } from './consent-actions';
import { ConsentAppHeader } from './consent-app-header';
import { ConsentScopes } from './consent-scopes';
import { ConsentUserRow } from './consent-user-row';

export function ConsentPage() {
  const {
    consentState,
    submitting,
    switching,
    decisionError,
    logoFailed,
    onLogoError,
    selectedLevel,
    onSelectLevel,
    switchUser,
    submitDecision,
  } = useConsent();

  const busy = submitting !== null || switching;

  let content: ReactNode;

  if (consentState.status === 'loading') {
    content = (
      <div className='flex justify-center py-4' data-testid='consent-loading'>
        <Spinner size={IconSizes.LG} />
      </div>
    );
  } else if (consentState.status === 'error') {
    content = (
      <div data-testid='consent-load-error'>
        <StatusMessage>{consentState.message}</StatusMessage>
      </div>
    );
  } else {
    content = (
      <>
        <ConsentAppHeader
          appName={consentState.info.app_name}
          tenantName={consentState.info.tenant_name}
          logoUri={consentState.info.app_logo_uri}
          logoFailed={logoFailed}
          onLogoError={onLogoError}
        />

        <ConsentUserRow
          userName={consentState.info.user_name}
          userEmail={consentState.info.user_email}
          disabled={busy}
          onSwitch={switchUser}
        />

        {consentState.info.access_levels?.length ? (
          <>
            <ConsentAccessLevels
              levels={consentState.info.access_levels}
              selectedLevel={selectedLevel}
              disabled={busy}
              onSelect={onSelectLevel}
            />
            <ConsentScopes scopes={nonMcpScopes(consentState.info.scopes)} />
          </>
        ) : (
          <ConsentScopes scopes={consentState.info.scopes} />
        )}

        {decisionError && (
          <div
            className='w-[352px] max-w-full'
            data-testid='consent-decision-error'
          >
            <StatusMessage centered={false}>{decisionError}</StatusMessage>
          </div>
        )}

        <ConsentActions
          allowLabel={
            consentState.info.access_levels?.length
              ? ACCESS_LEVEL_COPY[selectedLevel].allowLabel
              : 'Allow access'
          }
          submitting={submitting}
          disabled={busy}
          onAllow={() => submitDecision(true)}
          onDeny={() => submitDecision(false)}
        />
      </>
    );
  }

  return (
    <Page className='flex flex-col justify-center items-center bg-primary'>
      <Card className='w-[400px] max-w-full flex flex-col items-center gap-6 rounded-[0.75rem] border-secondary pt-8 px-6 pb-6 shadow-none'>
        {content}
      </Card>
    </Page>
  );
}
